<script>
import {
  h, reactive, ref, customRef, computed, mergeProps, watch,
} from 'vue';
import { COMPONENT_OUTSIDE_VIEW_EVENT } from '@common-vue/bag-checkout-common-consumable/src/utils/asyncComponent';

export const VIEWPORT_RENDERING_FINISHED = 'viewportRenderingFinished';

let internalId = 0;
function getNextId() {
  internalId += 1;
  return internalId;
}

function convertToOnEventName(eventName) {
  return `on${eventName.replace(/^([a-z])/, (match, p1) => p1.toUpperCase())}`;
}

const loadingInstances = reactive(new Map());

export default {
  name: 'LazyLoadComponent',
  props: {
    sequential: {
      type: Boolean,
    },
  },
  emits: [VIEWPORT_RENDERING_FINISHED],
  setup(props, { slots, emit }) {
    let doneRendering = false;
    const myId = getNextId();
    loadingInstances.set(myId, true);
    const allowedToRender = ref(!props.sequential || loadingInstances.keys().next().value === myId);
    if (!allowedToRender.value) {
      /*
        if renedering depends on other <LazyLoad> components
        then start watching the changes in the loadingInstances map
      */
      let doneWatchingOthers = watch(
        () => loadingInstances.keys().next().value,
        (firstKey) => {
          /*
            If the first key in the loadingInstances map is same as myId
            that means I can start rendering now
          */
          if (firstKey === myId) {
            allowedToRender.value = true;
            // not need to watch for loadingInstances changes anymore
            doneWatchingOthers();
            doneWatchingOthers = null;
          }
        },
      );
    }

    const currentlyRenderingComponent = customRef((track, trigger) => {
      let value;

      return {
        get() {
          track();
          return value;
        },

        set(newValue) {
          value = newValue;
          /*
           when currentlyRenderingComponent gets mounted
           then we call currentlyRenderingComponent.value = null
           and we need <LazyLoad> component to re-render itself
          */
          if (newValue === null) {
            trigger(value);
          }
        },
      };
    });
    const anyChildOutsideViewport = ref(false);
    const onComponentOutsideViewEvent = convertToOnEventName(COMPONENT_OUTSIDE_VIEW_EVENT);

    function isDynamicComponentWaitingToRender(component) {
      const { type, children } = component;
      if (type) {
        if (typeof type === 'object') {
          return !doneRendering && type.$$lazyLoaded$$ === true && type.$$isRendered$$ !== true;
        } if (Array.isArray(children)) {
          // if Any children is dynamic, return true, else return false
          return children.some((child) => isDynamicComponentWaitingToRender(child));
        }
      }
      return false;
    }

    const anyDynamicComponentWaitingToRender = computed(() => {
      if (doneRendering || anyChildOutsideViewport.value) {
        return false;
      }
      if (currentlyRenderingComponent.value) {
        return true;
      }

      const nextWaitingDynamicComponentIndex = (slots.default() || []).findIndex((component) => isDynamicComponentWaitingToRender(component));

      return nextWaitingDynamicComponentIndex !== -1;
    });

    function componentMounted(component) {
      component.type.$$isRendered$$ = true;
      currentlyRenderingComponent.value = null;
    }

    function childOutsideViewport() {
      anyChildOutsideViewport.value = true;
    }

    return () => {
      // if this <LazyLoad> is not done with rendering it's children that fit in the viewport
      if (props.sequential && !allowedToRender.value) {
        // if some previous <LazyLoad> components are still rendering
        return null;
      }

      // slots need to be accessed within this return method's scope
      const children = slots.default();
      // Is there any dynamic component waiting to be rendered in the view port
      const isWaiting = anyDynamicComponentWaitingToRender.value;
      if (!isWaiting && !doneRendering) {
        doneRendering = true;
        loadingInstances.delete(myId);
        emit(VIEWPORT_RENDERING_FINISHED);
      }

      // If one dynamic component is loading,
      // then we need to stop further components from rendering
      // otherwise, we will end up seeing the CLS issue ( https://web.dev/cls/ )
      let blockOtherComponents = false;

      const getMappedListOfChildern = (allChildren) => allChildren.map((child) => {
        if (isWaiting) {
          if (blockOtherComponents) {
            return null;
          }

          if (isDynamicComponentWaitingToRender(child)) {
            currentlyRenderingComponent.value = child;
            // If parent type is not object and if any Children component exists, then map all the children
            // This happens for Checkout where <component> renders multiple child
            if (typeof child.type !== 'object' && Array.isArray(child.children)) {
              child.children = getMappedListOfChildern(child.children);
            }
            blockOtherComponents = true;
            return h(child, mergeProps(
              // {
              // key: child.props.id
              // },
              child.props,
              // { lazy: true },
              {
                onVnodeMounted: componentMounted.bind(null, child),
                [onComponentOutsideViewEvent]: childOutsideViewport,
              },
            ));
          }
        }

        return child;
      });
      return getMappedListOfChildern(children);
    };
  },
};
</script>
